/**
 * StableBatchedMesh - three.js `BatchedMesh` with a stable draw-slot map and
 * run-merged sub-draws.
 *
 * Why the stable map: under multiview (IWSDK on Quest through the super-three
 * fork) texture uploads are deferred to the end of the frame. `BatchedMesh`
 * keeps its slot to instance map in a DataTexture and rebuilds it every frame
 * with the default `sortObjects` / `perObjectFrustumCulled`, so the CPU draw
 * list and the GPU map can be one frame apart, which draws a wrong instance
 * per slot for a frame. See docs/02-QUEST_PERFORMANCE.md §6.1 and the
 * repository-level multiview note.
 *
 * How: slot i is instance i, forever. The indirect texture is written once.
 * Hidden or frustum-culled instances become zero-count entries in the
 * multi-draw arrays, which the GPU skips.
 *
 * Why the run merge: session 2 on a Quest 3 measured the same scene at 11.11 ms
 * as 14 merged meshes and at 15.6 ms as about 2,200 sub-draws, whether those
 * were submitted through one `multiDrawElementsWEBGL` per system or as one
 * `drawElements` each. The cost is the number of sub-draws, not how they are
 * submitted. Adjacent slots whose geometry is contiguous in the shared index
 * buffer and whose per-instance data matches draw the same pixels as one wider
 * sub-draw, so they are emitted as one: the run's sub-draw is written at its
 * first slot and the rest of the run becomes zero counts. Slot i still means
 * instance i, the indirect texture is still written once, and `gl_DrawID` still
 * resolves to the run's first instance, whose colour and matrix the whole run
 * shares by construction. A part that is hidden, culled, tinted on its own or
 * given its own matrix simply ends the run it sits in.
 *
 * It reads the same private fields `BatchedMesh` itself uses, which pins the
 * three.js version; the `three` alias already does that.
 */
import { computeBatchedBoundsTree, disposeBatchedBoundsTree, type GeometryBVH } from "three-mesh-bvh";
import { BatchedMesh, type BufferGeometry, type Camera, type Color, type CoordinateSystem, Frustum, type Intersection, type Material, Matrix4, Mesh, Ray, type Raycaster, Sphere } from "three";

interface InstanceInfo {
  visible: boolean;
  active: boolean;
  geometryIndex: number;
}

interface GeometryInfo {
  start: number;
  count: number;
}

interface Internals {
  _instanceInfo: InstanceInfo[];
  _geometryInfo: GeometryInfo[];
  _multiDrawStarts: Int32Array;
  _multiDrawCounts: Int32Array;
  _multiDrawCount: number;
  _indirectTexture: { image: { data: Uint32Array }; needsUpdate: boolean };
  _visibilityChanged: boolean;
}

interface ArrayCameraLike extends Camera {
  isArrayCamera?: boolean;
  cameras?: Camera[];
}

/** `keys[i]` while nothing has overridden the instance's colour. */
const NO_COLOUR = -1;

/** Floats per frustum in the view cache: six planes of (normal xyz, constant). */
const VIEW_STRIDE = 24;

const _matrix = new Matrix4();
const _sphere = new Sphere();
const _inverse = new Matrix4();
const _localRay = new Ray();
/** Stand-in object a bounds tree raycasts as: its matrix is the instance's, its material the batch's. */
const _probe = new Mesh();
const _instanceHits: Intersection[] = [];
interface BatchedIntersection extends Intersection {
  batchId?: number;
}
const _frustum = new Frustum();
const _frustums: Frustum[] = [];

export class StableBatchedMesh extends BatchedMesh {
  /** Keep per-frame frustum culling (writes zero counts, never compacts). */
  cullPerFrame = true;
  /** Merge adjacent slots that share geometry adjacency and instance data into one sub-draw. */
  mergeRuns = true;
  /** The frustum-culled count of the last frame, for stats. */
  lastCulled = 0;
  /** Multi-draw entries submitted last frame; trailing zero entries are trimmed. */
  lastSlots = 0;
  /** Sub-draws the GPU actually ran last frame: the run count, not the slot count. */
  lastRuns = 0;

  private slotsReady = false;
  /** Local-space bounding sphere per instance as (x, y, z, radius). */
  private spheres = new Float32Array(0);
  private spheresReady = false;
  /** Union of every active instance sphere, for the whole-mesh early out. */
  private readonly bounds = new Sphere();
  /** Per-instance colour key; instances sharing one may share a sub-draw. */
  private keys = new Int32Array(0);
  /** 1 where `setMatrixAt` gave the instance a matrix of its own. */
  private posed = new Uint8Array(0);
  /** The frustum planes the last cull ran against. */
  private lastView = new Float64Array(0);
  /**
   * Something other than the camera or `_visibilityChanged` needs the next frame
   * rescanned: a level swap, a tint, a rebuilt cache. `BatchedMesh` does not flag
   * `setGeometryIdAt`, and with culling off the camera never differs, so without
   * this a swapped level would keep drawing the old geometry's index range.
   */
  private dirty = true;

  constructor(maxInstanceCount: number, maxVertexCount: number, maxIndexCount: number, material?: Material) {
    super(maxInstanceCount, maxVertexCount, maxIndexCount, material);
    // The two defaults that make the slot map churn every frame.
    this.perObjectFrustumCulled = false;
    this.sortObjects = false;
  }

  private get internals(): Internals {
    return this as unknown as Internals;
  }

  /** Any structural change (a new instance, a deleted one) invalidates the once-written map. */
  override addInstance(geometryId: number): number {
    this.slotsReady = false;
    this.invalidate();
    return super.addInstance(geometryId);
  }

  override deleteInstance(instanceId: number): this {
    this.slotsReady = false;
    this.invalidate();
    return super.deleteInstance(instanceId);
  }

  /** Adding geometry leaves every existing instance's sphere alone: no rebuild. */
  override addGeometry(geometry: BufferGeometry, reservedVertexCount?: number, reservedIndexCount?: number): number {
    return super.addGeometry(geometry, reservedVertexCount, reservedIndexCount);
  }

  /** Replacing a geometry's contents can move any instance that uses it. */
  override setGeometryAt(geometryId: number, geometry: BufferGeometry): number {
    this.invalidate();
    return super.setGeometryAt(geometryId, geometry);
  }

  /**
   * A level swap moves one instance onto other geometry. Only that instance's
   * sphere changes, so it is refreshed in place: a client swapping levels as the
   * viewer moves must not pay for a whole-batch rebuild every frame. Run
   * adjacency is read live each frame, so it needs nothing here.
   */
  override setGeometryIdAt(instanceId: number, geometryId: number): this {
    const result = super.setGeometryIdAt(instanceId, geometryId);
    this.refresh(instanceId);
    return result;
  }

  /** An instance with a matrix of its own can never share a sub-draw. */
  override setMatrixAt(instanceId: number, matrix: Matrix4): this {
    this.ensureKeys();
    if (instanceId >= 0 && instanceId < this.posed.length) this.posed[instanceId] = 1;
    const result = super.setMatrixAt(instanceId, matrix);
    this.refresh(instanceId);
    return result;
  }

  /**
   * Recompute one instance's cached sphere and widen the batch bounds to hold
   * it. Widening only ever makes both whole-batch early outs more cautious, so
   * a stale-but-larger bounds is safe; a rebuild squares it up next time the
   * batch changes shape.
   */
  private refresh(instanceId: number): void {
    this.dirty = true;
    if (!this.spheresReady) return;
    const it = this.internals;
    const info = it._instanceInfo[instanceId];
    if (info === undefined || instanceId * 4 + 3 >= this.spheres.length) {
      this.invalidate();
      return;
    }
    if (!info.active || it._geometryInfo[info.geometryIndex] === undefined || this.getBoundingSphereAt(info.geometryIndex, _sphere) === null) {
      this.invalidate();
      return;
    }
    this.getMatrixAt(instanceId, _matrix);
    _sphere.applyMatrix4(_matrix);
    const o = instanceId * 4;
    this.spheres[o] = _sphere.center.x;
    this.spheres[o + 1] = _sphere.center.y;
    this.spheres[o + 2] = _sphere.center.z;
    this.spheres[o + 3] = _sphere.radius;
    this.bounds.union(_sphere);
  }

  /** Tinting one part on its own splits it out of whatever run it sits in. */
  override setColorAt(instanceId: number, color: Color): void {
    this.ensureKeys();
    if (instanceId >= 0 && instanceId < this.keys.length) {
      const hex = color.getHex();
      if (this.keys[instanceId] !== hex) {
        this.keys[instanceId] = hex;
        this.dirty = true;
      }
    }
    super.setColorAt(instanceId, color);
  }

  override onBeforeRender(
    _renderer: unknown,
    _scene: unknown,
    camera: Camera,
    geometry: BufferGeometry,
    _material: Material,
    _group: unknown,
  ): void {
    const it = this.internals;
    const instances = it._instanceInfo;

    if (!this.slotsReady) {
      const ids = it._indirectTexture.image.data;
      for (let i = 0; i < instances.length; i++) ids[i] = i;
      it._indirectTexture.needsUpdate = true; // the only upload it ever needs
      this.slotsReady = true;
      it._visibilityChanged = true;
    }

    this.ensureCaches();
    const frustums = this.cullPerFrame ? this.localFrustums(camera) : [];

    // Nothing has changed since the last frame: the arrays already say what to draw.
    // `sameView` records the frustums either way, so a frame that changed visibility
    // still leaves the next one able to skip.
    const viewSame = this.sameView(frustums);
    if (!this.dirty && !it._visibilityChanged && viewSame) return;

    const index = geometry.getIndex();
    const bytesPerElement = index === null ? 1 : index.array.BYTES_PER_ELEMENT;
    const starts = it._multiDrawStarts;
    const counts = it._multiDrawCounts;
    const geometries = it._geometryInfo;

    // Whole-mesh early outs: one sphere test decides for every instance in the batch.
    let testEach = frustums.length > 0;
    if (testEach && !this.bounds.isEmpty()) {
      if (frustums.every((f) => this.outside(f, this.bounds))) {
        counts.fill(0, 0, instances.length);
        it._multiDrawCount = 0;
        it._visibilityChanged = false;
        this.dirty = false;
        this.lastCulled = instances.length;
        this.lastSlots = 0;
        this.lastRuns = 0;
        return;
      }
      if (frustums.some((f) => this.inside(f, this.bounds))) testEach = false;
    }

    let culled = 0;
    let runs = 0;
    let runSlot = -1;
    let runNext = 0; // the index element a run would continue at
    let lastRun = -1; // the slot the last run opened at

    for (let i = 0; i < instances.length; i++) {
      const info = instances[i]!;
      const g = geometries[info.geometryIndex];
      let drawn = info.active && info.visible && g !== undefined && g.count > 0;
      if (drawn && testEach) {
        const o = i * 4;
        _sphere.center.set(this.spheres[o]!, this.spheres[o + 1]!, this.spheres[o + 2]!);
        _sphere.radius = this.spheres[o + 3]!;
        let seen = false;
        for (const f of frustums) {
          if (f.intersectsSphere(_sphere)) {
            seen = true;
            break;
          }
        }
        if (!seen) {
          drawn = false;
          culled += 1;
        }
      }

      starts[i] = 0;
      counts[i] = 0;
      if (!drawn) {
        runSlot = -1; // a gap in the slot order ends the run
        continue;
      }

      const range = g!;
      if (this.mergeRuns && runSlot >= 0 && range.start === runNext && this.sharesData(i, runSlot)) {
        counts[runSlot] += range.count;
      } else {
        runSlot = i;
        lastRun = i;
        starts[i] = range.start * bytesPerElement;
        counts[i] = range.count;
        runs += 1;
      }
      runNext = range.start + range.count;
    }

    // Every entry past the last run's own slot is a zero count, so the submitted
    // range stops there. With the whole body visible that is one entry, not 2,229.
    it._multiDrawCount = lastRun + 1;
    it._visibilityChanged = false;
    this.dirty = false;
    this.lastCulled = culled;
    this.lastSlots = lastRun + 1;
    this.lastRuns = runs;
  }

  /**
   * Skip the whole batch when the ray cannot reach it.
   *
   * `BatchedMesh.raycast` walks every instance and raycasts each one's geometry
   * with no whole-mesh test first, so picking at the atlas would cost 2,229
   * per-geometry raycasts per system per pick even for a ray pointing away from
   * the body. The cull already keeps a union sphere of every instance, so one
   * sphere test against it settles most systems for nothing.
   */
  override raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    this.ensureCaches();
    if (!this.bounds.isEmpty()) {
      _sphere.copy(this.bounds).applyMatrix4(this.matrixWorld);
      if (!raycaster.ray.intersectsSphere(_sphere)) return;
    }
    const trees = this.boundsTrees;
    if (!trees || !this.spheresReady) {
      super.raycast(raycaster, intersects);
      return;
    }
    // Every instance's cached sphere lives in this batch's local space, so the
    // ray goes there once and the walk over 2,229 instances is a sphere test
    // each; only the few the ray crosses are queried through their trees.
    // (three-mesh-bvh's own batched path inverts a matrix per instance and
    // visits every one; measured on the real atlas that left a hit test at
    // 2 to 2.5 ms, most of it that per-instance work.)
    _inverse.copy(this.matrixWorld).invert();
    _localRay.copy(raycaster.ray).applyMatrix4(_inverse);
    const instances = this.internals._instanceInfo;
    const spheres = this.spheres;
    _probe.material = this.material;
    _probe.geometry = this.geometry;
    for (let i = 0; i < instances.length; i++) {
      const info = instances[i]!;
      if (!info.active || !info.visible) continue;
      const o = i * 4;
      if (o + 3 >= spheres.length) continue;
      _sphere.center.set(spheres[o]!, spheres[o + 1]!, spheres[o + 2]!);
      _sphere.radius = spheres[o + 3]!;
      if (!_localRay.intersectsSphere(_sphere)) continue;
      // a geometry added after the trees were built (a later level) gets its tree here, once
      this.ensureBoundsTree(info.geometryIndex);
      const tree = trees[info.geometryIndex];
      if (!tree) continue;
      this.getMatrixAt(i, _probe.matrixWorld).premultiply(this.matrixWorld);
      _instanceHits.length = 0;
      tree.raycastObject3D(_probe, raycaster, _instanceHits);
      for (const hit of _instanceHits) {
        hit.object = this;
        (hit as BatchedIntersection).batchId = i;
        intersects.push(hit);
      }
    }
  }

  /**
   * A bounds tree (three-mesh-bvh) per geometry, or null until `buildBoundsTrees`
   * runs. `BatchedMesh.raycast` walks every triangle of every instance whose
   * bounding box the ray crosses, and a ray through the torso crosses the
   * boxes of hundreds of parts: measured on the real atlas at the far level in
   * desktop Chromium, one hit test cost 3 to 4.5 ms, and the interaction
   * runtime asks for one per input source per frame. With a tree per geometry
   * the walk is logarithmic. The trees are indirect (their own triangle order),
   * so the shared index buffer the GPU holds is never reordered.
   */
  boundsTrees?: (GeometryBVH | null)[];

  /** Build a bounds tree for every geometry in this batch (about 10 to 60 ms per system on a desk). */
  buildBoundsTrees(): void {
    computeBatchedBoundsTree.call(this, -1, { indirect: true });
  }

  /** Build the tree for one geometry added after `buildBoundsTrees` (a later level). No-op before it. */
  ensureBoundsTree(geometryId: number): void {
    if (!this.boundsTrees || this.boundsTrees[geometryId]) return;
    computeBatchedBoundsTree.call(this, geometryId, { indirect: true });
  }

  disposeBoundsTrees(): void {
    if (this.boundsTrees) disposeBatchedBoundsTree.call(this, -1);
    this.boundsTrees = undefined;
  }

  /** Two instances may share a sub-draw when neither is posed and their colours match. */
  private sharesData(a: number, b: number): boolean {
    return this.posed[a] === 0 && this.posed[b] === 0 && this.keys[a] === this.keys[b];
  }

  /** Drop what the next frame must recompute. */
  private invalidate(): void {
    this.spheresReady = false;
    this.dirty = true;
  }

  private ensureKeys(): void {
    const n = Math.max(this.internals._instanceInfo.length, this.maxInstanceCount);
    if (this.keys.length >= n) return;
    const keys = new Int32Array(n);
    keys.fill(NO_COLOUR);
    keys.set(this.keys);
    this.keys = keys;
    const posed = new Uint8Array(n);
    posed.set(this.posed);
    this.posed = posed;
  }

  private ensureCaches(): void {
    this.ensureKeys();
    const it = this.internals;
    const n = it._instanceInfo.length;
    if (this.spheresReady && this.spheres.length >= n * 4) return;

    // Instance matrices and per-level geometry are fixed once a case is built, so
    // each instance's local-space sphere is computed here rather than every frame.
    const spheres = new Float32Array(Math.max(n, this.maxInstanceCount) * 4);
    const geometries = it._geometryInfo;
    let first = true;
    for (let i = 0; i < n; i++) {
      const info = it._instanceInfo[i]!;
      const g = geometries[info.geometryIndex];
      if (!info.active || g === undefined) continue;
      if (this.getBoundingSphereAt(info.geometryIndex, _sphere) === null) {
        // No sphere to test against: an infinite one is never culled and never
        // lets the whole-mesh early outs decide on this instance's behalf.
        _sphere.center.set(0, 0, 0);
        _sphere.radius = Infinity;
      } else {
        this.getMatrixAt(i, _matrix);
        _sphere.applyMatrix4(_matrix);
      }
      const o = i * 4;
      spheres[o] = _sphere.center.x;
      spheres[o + 1] = _sphere.center.y;
      spheres[o + 2] = _sphere.center.z;
      spheres[o + 3] = _sphere.radius;
      if (first) {
        this.bounds.copy(_sphere);
        first = false;
      } else {
        this.bounds.union(_sphere);
      }
    }
    if (first) this.bounds.makeEmpty();
    this.spheres = spheres;
    this.spheresReady = true;
    this.dirty = true;
  }

  /** True when every plane keeps the whole sphere on its inner side. */
  private inside(f: Frustum, s: Sphere): boolean {
    for (const p of f.planes) if (p.distanceToPoint(s.center) < s.radius) return false;
    return true;
  }

  /** True when one plane puts the whole sphere on its outer side. */
  private outside(f: Frustum, s: Sphere): boolean {
    for (const p of f.planes) if (p.distanceToPoint(s.center) < -s.radius) return true;
    return false;
  }

  /**
   * True when this cull would repeat the last one: same cameras, same pose. A
   * still head on the desk skips the whole scan. In VR it rarely fires, and the
   * comparison costs 24 floats per eye against a 2,000-instance loop.
   */
  private sameView(frustums: Frustum[]): boolean {
    const need = frustums.length * VIEW_STRIDE;
    if (this.lastView.length !== need) {
      this.lastView = new Float64Array(need);
      for (let i = 0; i < frustums.length; i++) this.writeView(frustums[i]!, i);
      return false;
    }
    for (let i = 0; i < frustums.length; i++) {
      const planes = frustums[i]!.planes;
      for (let p = 0; p < 6; p++) {
        const o = i * VIEW_STRIDE + p * 4;
        const plane = planes[p]!;
        if (this.lastView[o] !== plane.normal.x || this.lastView[o + 1] !== plane.normal.y || this.lastView[o + 2] !== plane.normal.z || this.lastView[o + 3] !== plane.constant) {
          for (let k = 0; k < frustums.length; k++) this.writeView(frustums[k]!, k);
          return false;
        }
      }
    }
    return true;
  }

  private writeView(f: Frustum, i: number): void {
    for (let p = 0; p < 6; p++) {
      const o = i * VIEW_STRIDE + p * 4;
      const plane = f.planes[p]!;
      this.lastView[o] = plane.normal.x;
      this.lastView[o + 1] = plane.normal.y;
      this.lastView[o + 2] = plane.normal.z;
      this.lastView[o + 3] = plane.constant;
    }
  }

  /** One frustum per camera (two under XR), expressed in this mesh's local space. */
  private localFrustums(camera: Camera): Frustum[] {
    const array = camera as ArrayCameraLike;
    const cameras = array.isArrayCamera && array.cameras && array.cameras.length > 0 ? array.cameras : [camera];
    while (_frustums.length < cameras.length) _frustums.push(new Frustum());
    const out: Frustum[] = [];
    for (let i = 0; i < cameras.length; i++) {
      const c = cameras[i]!;
      _matrix.multiplyMatrices(c.projectionMatrix, c.matrixWorldInverse).multiply(this.matrixWorld);
      const f = cameras.length === 1 ? _frustum : _frustums[i]!;
      f.setFromProjectionMatrix(_matrix, (c as { coordinateSystem?: CoordinateSystem }).coordinateSystem, (c as { reversedDepth?: boolean }).reversedDepth);
      out.push(f);
    }
    return out;
  }
}
